import { alpha } from '@mui/material';

import {
  AI_LAVENDER,
  BODY_TEXT,
  BRIGHT_BLUE,
  DARK_GREY,
  DARK_RED,
  DEEP_BLUE,
  GREEN,
  LEGACY_WIDGET_BACKGROUND,
  LIGHT_RED,
  PLACEHOLDER,
  RED,
  WHITE
} from '@/components/ThemeContext/constants';

import { FOCUS_OUTLINE, FONT } from '../aiStyles';

// Candidate-facing booking page in the candidate portal's frame
// (packs/candidate_portal PortalShell: centred company name, 36/28px heading,
// bordered white cards on a near-white page, legal footer). The portal's
// border literal is LEGACY_WIDGET_BACKGROUND and its page fill a tint of it.
//
// Container queries, not viewport ones, so the layout follows the width the
// page is actually given.
//
// AI purple and WCAG 2.2 AA throughout: DEEP_BLUE for selection, links, focus
// and key icons; AI_LAVENDER for bookable tints; BRIGHT_BLUE only for borders
// and decoration; DARK_GREY / DARK_RED for muted and error text.
const narrow = '@container booking (max-width: 899px)';
const stackedPicker = '@container picker (max-width: 619px)';

const PORTAL_BORDER = LEGACY_WIDGET_BACKGROUND;
const focusRing = {
  '&:focus-visible': { outline: FOCUS_OUTLINE, outlineOffset: '2px' }
};

// Scout field wrappers (FormTextField, FormVirtualizedAutocomplete) draw a
// DIVIDER border (1.4:1), a mid-grey label (3.8:1), a pale placeholder and a
// SCOUT_BLUE focus glow. Restyled from a wrapping Box: BRIGHT_BLUE border
// (3.0:1), DARK_GREY label and placeholder, a solid DEEP_BLUE focus ring,
// DARK_RED for the required star and error state.
const fieldOverrides = {
  '& .MuiInputLabel-root': { color: `${DARK_GREY} !important` },
  '& .MuiInputLabel-root span': { color: DARK_RED },
  '& .MuiInputBase-root .MuiInputBase-input::placeholder': { color: DARK_GREY },
  // FormTextField: the border is on the <input>.
  '& .MuiInputBase-root:not(.MuiAutocomplete-inputRoot) .MuiInputBase-input': {
    borderColor: BRIGHT_BLUE,
    '&:focus': { borderColor: DEEP_BLUE, boxShadow: `0 0 0 1px ${DEEP_BLUE}` }
  },
  '& .MuiInputBase-root.Mui-error:not(.MuiAutocomplete-inputRoot) .MuiInputBase-input': {
    borderColor: DARK_RED,
    '&:focus': { borderColor: DARK_RED, boxShadow: `0 0 0 1px ${DARK_RED}` }
  },
  // FormVirtualizedAutocomplete: the border is on the InputBase root.
  '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot': {
    borderColor: BRIGHT_BLUE,
    '&:focus-within': { borderColor: DEEP_BLUE, boxShadow: `0 0 0 1px ${DEEP_BLUE}` }
  }
};

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};

export const styles = {
  visuallyHidden,
  page: {
    backgroundColor: alpha(LEGACY_WIDGET_BACKGROUND, 0.2),
    containerName: 'booking',
    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: FONT,
    minHeight: '100vh',
    width: '100%'
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '72px',
    paddingX: 2,
    paddingY: 3,
    [narrow]: { paddingY: 2 }
  },
  companyName: {
    color: DARK_GREY,
    fontSize: '24px',
    fontWeight: 700,
    lineHeight: 1.25,
    maxWidth: '70vw',
    [narrow]: { fontSize: '20px' }
  },
  main: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    marginX: 'auto',
    maxWidth: '1180px',
    paddingBottom: 5,
    paddingTop: 2,
    paddingX: 3,
    width: '100%',
    [narrow]: { paddingBottom: 0, paddingTop: 1, paddingX: 2 }
  },
  titleBlock: {
    marginBottom: 4,
    rowGap: 1,
    [narrow]: { marginBottom: 3 }
  },
  centredTitleBlock: {
    alignItems: 'center',
    marginBottom: 0,
    textAlign: 'center',
    [narrow]: { marginBottom: 0 }
  },
  title: {
    color: DARK_GREY,
    fontSize: '36px',
    fontWeight: 700,
    lineHeight: 1.2,
    [narrow]: { fontSize: '28px' }
  },
  subtitle: {
    color: DARK_GREY,
    fontSize: '16px',
    lineHeight: 1.5
  },
  layout: {
    alignItems: 'start',
    columnGap: 4,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 300px',
    [narrow]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  mainColumn: {
    minWidth: 0,
    rowGap: 3,
    [narrow]: { rowGap: 2 }
  },
  sidebar: {
    position: 'sticky',
    top: '24px',
    [narrow]: { display: 'none' }
  },
  card: {
    backgroundColor: WHITE,
    border: `1px solid ${PORTAL_BORDER}`,
    borderRadius: '6px',
    boxSizing: 'border-box',
    padding: 3,
    rowGap: 2,
    [narrow]: { padding: 2 }
  },
  cardHeading: {
    color: DARK_GREY,
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: 1.35
  },
  introRow: {
    alignItems: 'flex-start',
    columnGap: 2,
    flexDirection: 'row'
  },
  introIcon: {
    alignItems: 'center',
    backgroundColor: AI_LAVENDER,
    borderRadius: '50%',
    color: DEEP_BLUE,
    display: 'flex',
    flexShrink: 0,
    height: '48px',
    justifyContent: 'center',
    width: '48px',
    [narrow]: { height: '40px', width: '40px', '& svg': { fontSize: '20px' } }
  },
  introLead: {
    color: BODY_TEXT,
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.4,
    textWrap: 'pretty',
    [narrow]: { fontSize: '16px' }
  },
  body: {
    color: DARK_GREY,
    fontSize: '16px',
    lineHeight: 1.5,
    textWrap: 'pretty',
    [narrow]: { fontSize: '15px' }
  },
  caption: {
    color: DARK_GREY,
    fontSize: '14px',
    lineHeight: 1.45
  },
  strong: {
    color: BODY_TEXT,
    fontSize: 'inherit',
    fontWeight: 600,
    lineHeight: 'inherit'
  },
  // Time zone line: "Times shown in Sydney time (AEDT, GMT+11:00) · Detected
  // from your device · Change".
  timeZoneRow: {
    alignItems: 'flex-start',
    columnGap: 1,
    flexDirection: 'row',
    '& > svg': { color: DARK_GREY, fontSize: '18px', marginTop: '1px' }
  },
  timeZoneText: {
    alignItems: 'center',
    columnGap: 1,
    display: 'flex',
    flexWrap: 'wrap',
    minWidth: 0,
    rowGap: 0
  },
  timeZoneActions: {
    alignItems: 'center',
    columnGap: 1,
    flexDirection: 'row'
  },
  // Separates the hint on one line; dropped once the hint wraps on phones.
  timeZoneLeadDot: {
    color: DARK_GREY,
    fontSize: '14px',
    [narrow]: { display: 'none' }
  },
  timeZoneHint: {
    color: DARK_GREY,
    fontSize: '14px'
  },
  timeZoneButton: {
    marginY: -0.75
  },
  timeZoneField: {
    maxWidth: '360px',
    width: '100%',
    ...fieldOverrides
  },
  pickerCard: {
    containerName: 'picker',
    containerType: 'inline-size'
  },
  pickerRow: {
    alignItems: 'flex-start',
    columnGap: 3,
    display: 'flex',
    rowGap: 3,
    [stackedPicker]: { flexDirection: 'column' }
  },
  calendarColumn: {
    flex: '0 0 320px',
    rowGap: 1,
    width: '320px',
    [stackedPicker]: { alignSelf: 'center', flex: '0 0 auto', maxWidth: '100%' }
  },
  // Static calendar in the Invite to timeslot style (centred month, arrows
  // either side), with an AI-lavender fill and bold DEEP_BLUE number on every
  // bookable day so they read as tappable on a phone (8.4:1); the picked day
  // is solid DEEP_BLUE with a white number (10.2:1).
  calendar: {
    '& .MuiDateCalendar-root': { height: 'auto', margin: 0, maxWidth: '100%', width: '320px' },
    '& .MuiPickersCalendarHeader-root': {
      marginTop: 0,
      paddingInline: 0.5,
      position: 'relative'
    },
    '& .MuiPickersCalendarHeader-label': {
      color: DARK_GREY,
      fontFamily: FONT,
      fontSize: '16px',
      fontWeight: 700
    },
    '& .MuiPickersArrowSwitcher-root': { justifyContent: 'space-between', width: '100%' },
    '& .MuiPickersArrowSwitcher-spacer': { flex: 1 },
    '& .MuiPickersArrowSwitcher-button:not(.Mui-disabled)': { color: DEEP_BLUE },
    '& .MuiPickersArrowSwitcher-button:focus-visible': { outline: FOCUS_OUTLINE },
    '& .MuiDayCalendar-weekDayLabel': {
      color: DARK_GREY,
      fontFamily: FONT,
      fontSize: '13px'
    },
    '& .MuiPickersDay-root': {
      color: DARK_GREY,
      fontFamily: FONT,
      fontSize: '14.5px',
      '&:focus-visible': { outline: FOCUS_OUTLINE, outlineOffset: '1px' }
    },
    '& .MuiPickersDay-root:not(.Mui-disabled):not(.Mui-selected)': {
      backgroundColor: AI_LAVENDER,
      color: DEEP_BLUE,
      fontWeight: 700,
      '&:hover': { backgroundColor: alpha(BRIGHT_BLUE, 0.3) }
    },
    '& .MuiPickersDay-root.Mui-disabled:not(.Mui-selected)': { color: PLACEHOLDER },
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: `${DEEP_BLUE} !important`,
      color: WHITE,
      fontWeight: 700
    }
  },
  laterDates: {
    alignItems: 'flex-start',
    borderTop: `1px solid ${PORTAL_BORDER}`,
    paddingTop: 1.5,
    rowGap: 0.5
  },
  showLaterButton: {
    marginLeft: -1
  },
  timesColumn: {
    flex: 1,
    minWidth: 0,
    rowGap: 2,
    width: '100%'
  },
  timesHeading: {
    color: DARK_GREY,
    fontSize: '16px',
    fontWeight: 700
  },
  periodGroup: {
    rowGap: 1
  },
  periodLabel: {
    color: DARK_GREY,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase'
  },
  slotGrid: {
    display: 'grid',
    gap: 1,
    gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
    [stackedPicker]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }
  },
  slotButton: {
    paddingX: 1,
    width: '100%'
  },
  unavailableSlot: {
    textDecoration: 'line-through'
  },
  notice: {
    alignItems: 'flex-start',
    backgroundColor: LIGHT_RED,
    border: `1px solid ${RED}`,
    borderRadius: '6px',
    columnGap: 1,
    flexDirection: 'row',
    padding: '10px 14px',
    '& > svg': { color: DARK_RED, fontSize: '20px', marginTop: '1px' }
  },
  noticeText: {
    color: BODY_TEXT,
    fontSize: '14px',
    lineHeight: 1.45
  },
  // FormRadioButtonGroup's unchecked ring is DIVIDER (1.4:1) and its checked
  // dot teal: DARK_GREY ring, DEEP_BLUE when checked, and a purple focus ring
  // in place of the faint focus ripple.
  radioGroup: {
    marginLeft: -1.5,
    marginY: -1,
    '& .MuiRadio-root': { color: DARK_GREY },
    '& .MuiRadio-root.Mui-checked': { color: DEEP_BLUE },
    '& .MuiRadio-root.Mui-focusVisible': { outline: FOCUS_OUTLINE, outlineOffset: '-4px' }
  },
  phoneField: {
    maxWidth: '400px',
    paddingLeft: 4,
    rowGap: 0.75,
    [narrow]: { maxWidth: 'none', paddingLeft: 0 },
    ...fieldOverrides
  },
  fieldError: {
    color: DARK_RED
  },
  // "Your call" summary (desktop sidebar).
  summaryList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    rowGap: 1.5
  },
  summaryRow: {
    alignItems: 'flex-start',
    columnGap: 1.25,
    flexDirection: 'row',
    '& > svg': { color: DEEP_BLUE, fontSize: '20px', marginTop: '1px' }
  },
  summaryValue: {
    color: BODY_TEXT,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.4
  },
  summaryPlaceholder: {
    color: DARK_GREY,
    fontSize: '15px',
    lineHeight: 1.4
  },
  summaryNote: {
    color: DARK_GREY,
    fontSize: '13px',
    lineHeight: 1.4
  },
  summaryDivider: {
    borderTop: `1px solid ${PORTAL_BORDER}`
  },
  confirmHint: {
    color: DARK_GREY,
    fontSize: '13px',
    textAlign: 'center'
  },
  // Phones: the summary and Confirm stick to the bottom of the screen, so the
  // candidate never has to scroll past ~30 slots to finish.
  mobileBar: {
    display: 'none',
    [narrow]: {
      alignItems: 'center',
      backgroundColor: WHITE,
      borderTop: `1px solid ${PORTAL_BORDER}`,
      bottom: 0,
      columnGap: 2,
      display: 'flex',
      flexDirection: 'row',
      marginTop: 3,
      marginX: -2,
      paddingX: 2,
      paddingY: 1.5,
      position: 'sticky',
      zIndex: 2
    }
  },
  mobileBarText: {
    flex: 1,
    minWidth: 0
  },
  mobileBarMain: {
    color: BODY_TEXT,
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: 1.3
  },
  mobileBarPlaceholder: {
    color: DARK_GREY,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.3
  },
  mobileBarSub: {
    color: DARK_GREY,
    fontSize: '13px',
    lineHeight: 1.35
  },
  mobileBarError: {
    color: DARK_RED,
    fontSize: '13px',
    lineHeight: 1.35
  },
  // Confirmed / already booked.
  narrowColumn: {
    marginX: 'auto',
    maxWidth: '640px',
    rowGap: 3,
    width: '100%',
    [narrow]: { paddingBottom: 4, rowGap: 2 }
  },
  outcomeHeader: {
    alignItems: 'center',
    rowGap: 1,
    textAlign: 'center'
  },
  successIcon: {
    color: GREEN,
    fontSize: '56px'
  },
  bookedIcon: {
    color: DEEP_BLUE,
    fontSize: '56px'
  },
  detailList: {
    border: `1px solid ${PORTAL_BORDER}`,
    borderRadius: '6px',
    margin: 0
  },
  detailRow: {
    alignItems: 'baseline',
    columnGap: 2,
    display: 'grid',
    gridTemplateColumns: '130px minmax(0, 1fr)',
    paddingX: 2,
    paddingY: 1.5,
    '&:not(:first-of-type)': { borderTop: `1px solid ${PORTAL_BORDER}` },
    [narrow]: { gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 0.25 }
  },
  detailTerm: {
    color: DEEP_BLUE,
    fontSize: '14px',
    fontWeight: 700,
    margin: 0
  },
  detailValue: {
    color: BODY_TEXT,
    fontSize: '16px',
    margin: 0
  },
  detailNote: {
    color: DARK_GREY,
    fontSize: '14px'
  },
  outcomeActions: {
    alignItems: 'flex-start'
  },
  contactLine: {
    color: DARK_GREY,
    fontSize: '16px',
    lineHeight: 1.5,
    textAlign: 'center',
    textWrap: 'pretty'
  },
  // Expired / cancelled / invalid.
  stateCard: {
    alignItems: 'center',
    paddingBottom: 5,
    paddingTop: 2,
    textAlign: 'center'
  },
  stateIcon: {
    color: BRIGHT_BLUE,
    fontSize: '56px'
  },
  emptyStateText: {
    color: DARK_GREY,
    fontSize: '22px'
  },
  emptyStateWrapper: {
    paddingBottom: '4px',
    paddingTop: '24px'
  },
  stateDescription: {
    color: DARK_GREY,
    fontSize: '16px',
    lineHeight: 1.5,
    maxWidth: '440px',
    textWrap: 'pretty'
  },
  // No title above the invalid-link card, so give it room under the header.
  invalidColumn: {
    paddingTop: 3
  },
  footer: {
    alignItems: 'center',
    borderTop: `1px solid ${PORTAL_BORDER}`,
    columnGap: 2,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 4,
    paddingX: 5,
    paddingY: 3,
    rowGap: 1,
    [narrow]: { justifyContent: 'center', marginTop: 0, paddingX: 2 }
  },
  footerLinks: {
    alignItems: 'center',
    columnGap: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: 1
  },
  footerText: {
    color: DARK_GREY,
    fontSize: '14px'
  },
  footerLink: {
    color: DARK_GREY,
    fontSize: '14px',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
    ...focusRing
  }
};
